import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { EyeIcon, LockIcon } from 'lucide-react';
import { HealthConsentModal } from './HealthConsentModal';
import { SettingsGroup, SettingsRow, SettingsToggleRow } from './SettingsList';
import { SectionTitle } from './ui/Card';
import { Switch } from './ui/Switch';
import { biomarkerDefs } from '../data/biomarkers';
import { privacyPrefs, type PrivacyPref } from '../data/settings';
import { metricsForSport, sports } from '../data/sportMetrics';
import { useBiomarkers } from '../contexts/BiomarkersContext';
import { useLocale } from '../contexts/LocaleContext';
import { useMetrics } from '../contexts/MetricsContext';
import { useSettings } from '../contexts/SettingsContext';
import { formatMetric } from '../utils/units';
interface Item {
  id: string;
  label: string;
  detail?: string;
  checked: boolean;
  toggle: (next: boolean) => void;
}

/**
 * "O que aparece no meu perfil público" — one toggle per category, then
 * granular selection inside sport, body and biomarker categories.
 * Shared by Edit Profile and Privacy. Health categories need explicit consent.
 * Everything except achievements, badges and streak starts OFF.
 */
export function PublicVisibilityControls() {
  const {
    t,
    tl
  } = useLocale();
  const navigate = useNavigate();
  const settings = useSettings();
  const metrics = useMetrics();
  const bio = useBiomarkers();
  const [consentFor, setConsentFor] = useState<string | null>(null);
  const categories = privacyPrefs.filter(pref => pref.scope === 'public' && (!pref.parent || settings.privacy[pref.parent]));
  const pending = privacyPrefs.find(pref => pref.id === consentFor);
  const itemsFor = (pref: PrivacyPref): Item[] => {
    if (pref.granular === 'sport') {
      return sports.flatMap(sport => metricsForSport(sport).filter(metric => metric.value > 0).map(metric => ({
        id: metric.id,
        label: `${t(`category.${sport}`)} · ${tl(metric.label)}`,
        detail: formatMetric(metric.kind, metric.value, metrics.unitSystem),
        checked: metrics.isPublic(metric.id),
        toggle: () => metrics.togglePublic(metric.id)
      })));
    }
    if (pref.granular === 'body') {
      return metrics.bodyMetrics.filter(metric => !metrics.isHidden(metric.id)).map(metric => ({
        id: metric.id,
        label: t(`metrics.${metric.id}`),
        detail: formatMetric(metric.kind, metric.value, metrics.unitSystem),
        checked: metrics.isPublic(metric.id),
        toggle: () => metrics.togglePublic(metric.id)
      }));
    }
    if (pref.granular === 'biomarker') {
      return biomarkerDefs.map(def => ({
        id: def.id,
        label: tl(def.name),
        checked: bio.isPublic(def.id),
        toggle: (next: boolean) => bio.setPublic(def.id, next)
      }));
    }
    return [];
  };
  return <section data-id="element-1140" data-mp="3l6o3dnzbtbug">
      <SectionTitle data-id="element-1141" data-mp="3l6eb1tqt6sll">{t('visibility.title')}</SectionTitle>
      <p className="-mt-1 mb-2 text-[11px] leading-snug text-muted" data-id="element-1142" data-mp="3dnwzzj3ab5qi">
        {t('visibility.subtitle')}
      </p>

      <SettingsGroup data-id="element-1143" data-mp="38qenjiq268dr">
        {categories.map(pref => {
        const on = settings.privacy[pref.id];
        const items = on ? itemsFor(pref) : [];
        return <React.Fragment key={pref.id} data-id="element-1144" data-mp="2zo5detypch0w">
              <SettingsToggleRow label={pref.parent ? `— ${tl(pref.label)}` : tl(pref.label)} hint={tl(pref.hint)} checked={on} emphasis={pref.health} onChange={next => {
            if (next && pref.health) setConsentFor(pref.id);else settings.togglePrivacy(pref.id);
          }} data-id="element-1145" data-mp="2zrb2483f5gw3" />
              {items.length > 0 && <li className="border-b border-sand-line pb-2 last:border-b-0" data-id="element-1146" data-mp="2bzvy8avzuriq">
                  {items.every(item => !item.checked) ? <div className="mb-1 ml-3 flex flex-wrap items-center gap-x-2 gap-y-1 rounded-card border border-gold/35 bg-white/60 px-2 py-2" data-id="element-1147" data-mp="271b4mub8z639">
                      <p className="min-w-0 flex-1 basis-[160px] text-[11.5px] leading-snug text-deep" data-id="element-1148" data-mp="fwzmsw0azwo4">
                        {t('visibility.noneSelected')}
                      </p>
                      <button type="button" onClick={() => {
                const first = document.querySelector<HTMLElement>(`#pick-${pref.id} button`);
                first?.focus();
                first?.scrollIntoView({
                  block: 'center',
                  behavior: 'smooth'
                });
              }} className="min-h-[36px] shrink-0 rounded-pill border border-deep/20 px-2 text-[11.5px] font-medium text-deep transition-colors duration-150 ease-brand hover:border-gold" data-id="element-1149" data-mp="66eqjf83r48d">
                        {t('visibility.chooseMetrics')}
                      </button>
                    </div> : <p className="mb-1 pl-3 text-[10.5px] font-medium text-muted" data-id="element-1150" data-mp="321j8pxompqcp">
                      {t('visibility.pickEach')}
                    </p>}
                  <ul id={`pick-${pref.id}`} className="ml-3 rounded-card bg-sand-deep/50 px-2" data-id="element-1151" data-mp="3bv04364ezdih">
                    {items.map(item => <li key={item.id} className="flex min-h-[48px] items-center gap-2 border-b border-sand-line/70 py-1 last:border-b-0" data-id="element-1152" data-mp="3ku3pigr20505">
                        <span className="min-w-0 flex-1" data-id="element-1153" data-mp="3ut2vtsocjojz">
                          <span className="block truncate text-[12.5px] font-medium text-deep" data-id="element-1154" data-mp="3uqgp1v9a5d2t">
                            {item.label}
                          </span>
                          {item.detail && <span className="bee-num block text-[11px] text-muted" data-id="element-1155" data-mp="1vffdigmdkp2n">
                              {item.detail}
                            </span>}
                        </span>
                        <span className={`flex shrink-0 items-center gap-1 text-[10.5px] font-medium ${item.checked ? 'text-gold-deep' : 'text-muted'}`} data-id="element-1156" data-mp="2p4mtfya62njz">
                          {item.checked ? <EyeIcon size={12} aria-hidden="true" data-id="element-1157" data-mp="3438fbqarfzfs" /> : <LockIcon size={12} aria-hidden="true" data-id="element-1158" data-mp="3838traxv0amu" />}
                          {t(item.checked ? 'metrics.publicOn' : 'metrics.publicOff')}
                        </span>
                        <Switch checked={item.checked} onChange={item.toggle} label={item.label} data-id="element-1159" data-mp="3hsimmt0j1xc0" />
                      </li>)}
                  </ul>
                </li>}
            </React.Fragment>;
      })}
      </SettingsGroup>

      <p className="mt-2 flex items-start gap-1 px-1 text-[11px] leading-snug text-muted" data-id="element-1160" data-mp="1b51tap16499d">
        <LockIcon size={12} className="mt-[1px] shrink-0" aria-hidden="true" data-id="element-1161" data-mp="165yn319gbwzt" />
        {t('visibility.healthNote')}
      </p>

      <div className="mt-3" data-id="element-1162" data-mp="118gni1hbqu4h">
        <SettingsGroup data-id="element-1163" data-mp="1198baq4ojnk7">
          <SettingsRow label={t('visibility.preview')} icon={<EyeIcon size={17} data-id="element-1165" data-mp="1yue3ns88end9" />} onClick={() => navigate('/perfil/publico')} data-id="element-1164" data-mp="1yv5vcrypk0y9" />
        </SettingsGroup>
      </div>

      <HealthConsentModal subject={pending ? tl(pending.label) : null} onClose={() => setConsentFor(null)} onAccept={() => {
      if (consentFor) settings.togglePrivacy(consentFor);
      setConsentFor(null);
    }} data-id="element-1166" data-mp="1p52uy6p9bkpw" />
    </section>;
}